import { TextureLoader, SkeletonHelper } from 'three';
import * as Three from 'three';
import { Reflector } from 'three/examples/jsm/objects/Reflector.js';
import {
  collectMapExportExtrasFromObject,
  collectMaterialMapList,
  configureMaterialMapTexture,
  copyMaterialMapsBetweenObjects,
  ensureMapUserData,
  isPublishableTextureUrl,
  isTextureReferencedOnMaterial,
  materialHasMapTexture,
  objectHasEmbeddedGltfMaps,
  MAP_BACKUP_USERDATA_KEY,
  MAP_PREVIEW_URLS_USERDATA_KEY,
  MAP_VISIBILITY_USERDATA_KEY,
  restoreMaterialMapsFromSettings,
  setMaterialMapVisibility,
  syncMaterialMapBackupsFromSlots,
} from '../../utils/ThreeJsUtils/materialMaps';
import { collectChildMaterialOverrides, applyChildMaterialOverrides, syncChildMaterialOverridesToAssetRoot } from '../../utils/ThreeJsUtils/materialPropertyEditor';
import store from '../../redux/store';
import { updateSceneMeshMaterialSettings } from '../../redux/Slice/gridSlice';

export const determineObjectType = (object) => {
  if (object?.isMesh) {
    return 'Mesh';
  } else if (object?.type === 'Group' || object?.type === 'Scene') {
    return 'Group';
  }
  return 'Unknown';
};

export const MATERIAL_SIDE_OPTIONS = [
  { value: 'Front', label: 'Front' },
  { value: 'Back', label: 'Back' },
  { value: 'Double', label: 'Double' },
];

/** Three.js material class names (Material Type dropdown). */
export const MATERIAL_TYPE_OPTIONS = [
  { value: 'MeshBasicMaterial', label: 'MeshBasicMaterial' },
  { value: 'MeshDepthMaterial', label: 'MeshDepthMaterial' },
  { value: 'MeshNormalMaterial', label: 'MeshNormalMaterial' },
  { value: 'MeshLambertMaterial', label: 'MeshLambertMaterial' },
  { value: 'MeshMatcapMaterial', label: 'MeshMatcapMaterial' },
  { value: 'MeshPhongMaterial', label: 'MeshPhongMaterial' },
  { value: 'MeshToonMaterial', label: 'MeshToonMaterial' },
  { value: 'MeshStandardMaterial', label: 'MeshStandardMaterial' },
  { value: 'MeshPhysicalMaterial', label: 'MeshPhysicalMaterial' },
  { value: 'RawShaderMaterial', label: 'RawShaderMaterial' },
  { value: 'ShaderMaterial', label: 'ShaderMaterial' },
  { value: 'ShadowMaterial', label: 'ShadowMaterial' },
];

const LEGACY_SHADER_TO_TYPE = {
  Standard: 'MeshStandardMaterial',
  PBR: 'MeshPhysicalMaterial',
  Unlit: 'MeshBasicMaterial',
};

const TYPE_TO_LEGACY_SHADER = {
  MeshStandardMaterial: 'Standard',
  MeshPhysicalMaterial: 'PBR',
  MeshBasicMaterial: 'Unlit',
};

export function resolveMaterialType(materialType, shader) {
  if (materialType && MATERIAL_TYPE_OPTIONS.some((o) => o.value === materialType)) {
    return materialType;
  }
  if (shader && LEGACY_SHADER_TO_TYPE[shader]) {
    return LEGACY_SHADER_TO_TYPE[shader];
  }
  return 'MeshStandardMaterial';
}

/** Read mesh shadow flags from the selected object (first mesh). */
export function readShadowFromObject(object) {
  let castShadow = true;
  let receiveShadow = true;

  const visit = (mesh) => {
    if (!mesh?.isMesh) return false;
    castShadow = !!mesh.castShadow;
    receiveShadow = !!mesh.receiveShadow;
    return true;
  };

  if (object?.isMesh && visit(object)) {
    return { castShadow, receiveShadow };
  }

  let found = false;
  object?.traverse?.((child) => {
    if (!found && visit(child)) found = true;
  });

  return { castShadow, receiveShadow };
}

/** Apply cast/receive shadow to all meshes under the object. */
export function applyShadowSettingsToObject(object, { castShadow, receiveShadow }) {
  if (!object) return;

  const apply = (mesh) => {
    if (!mesh?.isMesh) return;
    if (castShadow !== undefined) mesh.castShadow = !!castShadow;
    if (receiveShadow !== undefined) mesh.receiveShadow = !!receiveShadow;
  };

  if (object.isMesh) apply(object);
  object.traverse?.((child) => apply(child));
}

export function readMaterialTypeFromObject(object) {
  let materialType = 'MeshStandardMaterial';

  const visit = (mesh) => {
    if (!mesh?.isMesh || !mesh.material) return false;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const mat = mats.find(Boolean);
    if (!mat?.type) return false;
    materialType = mat.type;
    return true;
  };

  if (object?.isMesh && visit(object)) return materialType;
  let found = false;
  object?.traverse?.((child) => {
    if (!found && visit(child)) found = true;
  });
  return materialType;
}

export function threeSideToLabel(side) {
  if (side === Three.BackSide) return 'Back';
  if (side === Three.DoubleSide) return 'Double';
  return 'Front';
}

export function labelToThreeSide(label) {
  const v = String(label || '').toLowerCase();
  if (v === 'back') return Three.BackSide;
  if (v === 'double') return Three.DoubleSide;
  return Three.FrontSide;
}

/** Apply base color to Three.js material(s) and keep rendering-mode snapshot in sync. */
export function applyAlbedoColorToMaterial(material, albedoHex) {
  if (!material) return;
  const hex =
    albedoHex && typeof albedoHex === 'string' ? albedoHex : '#ffffff';
  const mats = Array.isArray(material) ? material : [material];

  mats.forEach((mat) => {
    if (!mat?.color) return;
    try {
      mat.color.set(hex);
    } catch {
      mat.color.set(new Three.Color(hex));
    }
    if (mat.userData?.originalMaterialProps && mat.color) {
      mat.userData.originalMaterialProps.color = mat.color.getHex();
    }
    mat.needsUpdate = true;
  });
}

/** Apply albedo to every mesh material under the selected object (mesh, shape group, GLB child). */
export function applyAlbedoColorToObject(object, albedoHex) {
  if (!object) return;

  const applyToMesh = (mesh) => {
    if (mesh?.isMesh && mesh.material) {
      applyAlbedoColorToMaterial(mesh.material, albedoHex);
    }
  };

  applyToMesh(object);
  object.traverse?.((child) => applyToMesh(child));
}

export const MATERIAL_BLENDING_OPTIONS = [
  { value: 'Normal', label: 'Normal' },
  { value: 'Additive', label: 'Additive' },
  { value: 'Subtractive', label: 'Subtractive' },
  { value: 'Multiply', label: 'Multiply' },
];

export function labelToThreeBlending(label) {
  const v = String(label || '').toLowerCase();
  if (v === 'additive') return Three.AdditiveBlending;
  if (v === 'subtractive') return Three.SubtractiveBlending;
  if (v === 'multiply') return Three.MultiplyBlending;
  return Three.NormalBlending;
}

/** Read side / opacity / transparent from the first mesh material on the object. */
export function readMaterialSurfaceFromObject(object) {
  let side = 'Front';
  let opacity = 1;
  let transparency = false;

  const visit = (mesh) => {
    if (!mesh?.isMesh || !mesh.material) return false;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const mat = mats.find(Boolean);
    if (!mat) return false;
    side = threeSideToLabel(mat.side);
    opacity =
      mat.opacity !== undefined && mat.opacity !== null ? mat.opacity : 1;
    transparency = !!mat.transparent;
    return true;
  };

  if (object?.isMesh && visit(object)) {
    return { side, opacity, alpha: opacity, transparency };
  }

  let found = false;
  object?.traverse?.((child) => {
    if (!found && visit(child)) found = true;
  });

  return { side, opacity, alpha: opacity, transparency };
}

/**
 * Infer panel Rendering Mode from live Three.js material flags.
 * Used when material_settings.renderingMode is missing.
 */
export function inferRenderingModeFromMaterial(material) {
  if (!material) return 'Opaque';
  if (material.wireframe) return 'Wireframe';
  const alphaTest =
    typeof material.alphaTest === 'number' ? material.alphaTest : 0;
  const opacity =
    typeof material.opacity === 'number' ? material.opacity : 1;
  if (alphaTest > 1e-4) return 'Cutout';
  if (material.transparent && opacity < 0.999) return 'Fade';
  return 'Opaque';
}

export function inferRenderingModeFromObject(object) {
  let mode = 'Opaque';
  const visit = (mesh) => {
    if (!mesh?.isMesh || !mesh.material) return false;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const mat = mats.find(Boolean);
    if (!mat) return false;
    mode = inferRenderingModeFromMaterial(mat);
    return true;
  };
  if (object?.isMesh && visit(object)) return mode;
  let found = false;
  object?.traverse?.((child) => {
    if (!found && visit(child)) found = true;
  });
  return mode;
}

/**
 * OBJ/FBX loaders often leave transparent/alphaTest set even for solid meshes.
 * GLTFExporter then writes BLEND/MASK and objects appear as Cutout/Fade in the grid.
 * Clear false positives before OBJ/FBX → GLB conversion.
 */
export function normalizeImportedMeshMaterialsForExport(root) {
  if (!root?.traverse) return;

  root.traverse((child) => {
    if (!child.isMesh && !child.isSkinnedMesh) return;
    const materials = Array.isArray(child.material)
      ? child.material
      : [child.material];

    materials.forEach((mat) => {
      if (!mat) return;

      const opacity =
        typeof mat.opacity === 'number' && Number.isFinite(mat.opacity)
          ? Math.max(0, Math.min(1, mat.opacity))
          : 1;
      const alphaTest =
        typeof mat.alphaTest === 'number' && Number.isFinite(mat.alphaTest)
          ? mat.alphaTest
          : 0;

      // Treat near-opaque FBX/OBJ materials as Opaque for export.
      // Keep real translucent materials (opacity clearly below 1).
      const intentionallyTransparent = opacity < 0.999;
      const intentionallyCutout = intentionallyTransparent
        ? false
        : alphaTest > 1e-4 && !!mat.alphaMap;

      if (!intentionallyTransparent && !intentionallyCutout) {
        mat.transparent = false;
        mat.opacity = 1;
        mat.alphaTest = 0;
        if (mat.depthWrite !== undefined) mat.depthWrite = true;
        // Spurious FBX TransparentColor maps cause BLEND/Cutout after GLB convert.
        if (mat.alphaMap && opacity >= 0.999) {
          mat.alphaMap = null;
        }
        mat.needsUpdate = true;
      } else if (intentionallyTransparent) {
        mat.opacity = opacity;
        mat.transparent = true;
        mat.alphaTest = 0;
        if (mat.depthWrite !== undefined) mat.depthWrite = false;
        mat.needsUpdate = true;
      }

      mat.userData = mat.userData || {};
      const settings = mat.userData.material_settings || {};
      mat.userData.material_settings = {
        ...settings,
        renderingMode: intentionallyCutout
          ? 'Cutout'
          : intentionallyTransparent
            ? 'Fade'
            : 'Opaque',
        transparency: intentionallyTransparent || intentionallyCutout,
        opacity: intentionallyTransparent ? opacity : 1,
        alpha: intentionallyTransparent ? opacity : 1,
      };
    });
  });
}

/** Whether material_settings enables transparency (boolean or legacy Alpha/Blend strings). */
export function resolveMaterialTransparencyFlag(settings) {
  if (!settings || settings.transparency === undefined) return undefined;
  const t = settings.transparency;
  if (
    t === true ||
    t === 'Alpha' ||
    t === 'Blend' ||
    t === 'alpha' ||
    t === 'blend'
  ) {
    return true;
  }
  if (t === false || t === 'Opaque' || t === '') return false;
  return !!t;
}

/** Map stored material_settings to Three.js material surface props. */
export function materialSettingsToThreeProps(settings, ThreeNS = Three) {
  const s = settings || {};
  const transparent = resolveMaterialTransparencyFlag(s);
  const opacity =
    s.opacity !== undefined
      ? Number(s.opacity)
      : s.alpha !== undefined
        ? Number(s.alpha)
        : 1;
  const resolvedOpacity = Math.max(
    0,
    Math.min(1, Number.isFinite(opacity) ? opacity : 1)
  );
  const isTransparent = transparent !== undefined ? transparent : true;

  return {
    side:
      s.side !== undefined ? labelToThreeSide(s.side) : ThreeNS.DoubleSide,
    transparent: isTransparent,
    opacity: resolvedOpacity,
    depthWrite: !isTransparent,
  };
}

/** Re-apply side / opacity / transparency after rendering-mode (which can reset them). */
export function reapplyMaterialSurfaceFromSettings(materials, data) {
  if (!data || !materials?.length) return;
  // Wireframe mode owns opacity/transparent/depthWrite — don't undo it
  // (panel opacity/transparency from Fade etc. made wires vanish).
  if (String(data.renderingMode) === 'Wireframe') return;

  const transparent = resolveMaterialTransparencyFlag(data);
  const side =
    data.side !== undefined ? labelToThreeSide(data.side) : undefined;
  const opacity =
    data.opacity !== undefined
      ? data.opacity
      : data.alpha !== undefined
        ? data.alpha
        : undefined;

  const applyOne = (material) => {
    if (!material) return;
    if (transparent !== undefined) {
      material.transparent = transparent;
      material.depthWrite = !transparent;
    }
    if (side !== undefined) material.side = side;
    if (opacity !== undefined) {
      material.opacity = Math.max(0, Math.min(1, opacity));
    }
    material.needsUpdate = true;
  };

  materials.forEach((material) => {
    if (Array.isArray(material)) material.forEach(applyOne);
    else applyOne(material);
  });
}

const PLACEHOLDER_ALBEDO_VALUES = new Set([
  '',
  '#00FFFFFF',
  '#FFFFFF',
  '#FFFFFFFF',
]);

export function colorToAlbedoHex(color) {
  if (!color) return '';
  if (typeof color.getHexString === 'function') {
    return `#${color.getHexString()}`.toUpperCase();
  }
  if (typeof color.getHex === 'function') {
    const hex = color.getHex();
    return `#${hex.toString(16).padStart(6, '0')}`.toUpperCase();
  }
  return '';
}

export function isPlaceholderAlbedo(value) {
  if (value === undefined || value === null) return true;
  return PLACEHOLDER_ALBEDO_VALUES.has(String(value).toUpperCase());
}

export function threeBlendingToLabel(blending) {
  if (blending === Three.AdditiveBlending) return 'Additive';
  if (blending === Three.SubtractiveBlending) return 'Subtractive';
  if (blending === Three.MultiplyBlending) return 'Multiply';
  return 'Normal';
}

function readMaterialScalarsFromMat(mat, storedSettings) {
  // Always prefer live material appearance. originalMaterialProps is only used
  // when switching rendering modes — using it here made duplicates paint the
  // pre-edit shape default colors (cyan torus, orange capsule, etc.).
  const albedo = colorToAlbedoHex(mat.color);

  let metalness = 0.5;
  let roughness = 0.5;
  if (typeof mat.metalness === 'number') {
    metalness = mat.metalness;
  } else if (typeof storedSettings?.metallic?.metalness === 'number') {
    metalness = storedSettings.metallic.metalness;
  }

  if (typeof mat.roughness === 'number') {
    roughness = mat.roughness;
  } else if (typeof storedSettings?.metallic?.roughness === 'number') {
    roughness = storedSettings.metallic.roughness;
  }

  const emission = colorToAlbedoHex(mat.emissive);

  const metalicType = !!(mat.metalnessMap || mat.roughnessMap);
  // Do not treat default emissiveIntensity (often 1) as emission on.
  // Emission is on only when there is an emissive map or a non-black emissive color.
  const hasEmissiveColor =
    !!mat.emissive &&
    (mat.emissive.r > 0.001 ||
      mat.emissive.g > 0.001 ||
      mat.emissive.b > 0.001);
  const emissionType = !!(mat.emissiveMap || hasEmissiveColor);

  return {
    albedo,
    metallic: {
      metalness,
      roughness,
      map: storedSettings?.metallic?.map ?? '',
    },
    emission: emission || '',
    metalicType,
    emissionType,
    blending: threeBlendingToLabel(mat.blending),
  };
}

/**
 * Read albedo, metalness, roughness, emission, etc. from the first mesh material on the object.
 */
export function readMaterialPanelStateFromObject(object, storedSettings = null) {
  const settings =
    storedSettings ?? object?.userData?.material_settings ?? null;

  let result = null;

  const visit = (mesh) => {
    if (!mesh?.isMesh || !mesh.material) return false;
    const mats = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];
    const mat = mats.find(Boolean);
    if (!mat) return false;
    result = readMaterialScalarsFromMat(mat, settings);
    return true;
  };

  if (object?.isMesh && visit(object)) {
    return result;
  }

  object?.traverse?.((child) => {
    if (!result && visit(child)) {
      /* first mesh wins */
    }
  });

  return (
    result || {
      albedo: '',
      metallic: { metalness: 0.5, roughness: 0.5, map: '' },
      emission: '',
      metalicType: false,
      emissionType: false,
      blending: 'Normal',
    }
  );
}

/**
 * Merge stored material_settings with live Three.js material values for the panel.
 */
export function buildMaterialPanelStateForSelection(
  object,
  storedSettings,
  { selectionId, meshes } = {}
) {
  const settings = storedSettings ?? object?.userData?.material_settings ?? null;
  const surface = readMaterialSurfaceFromObject(object);
  const shadow = readShadowFromObject(object);
  const live = readMaterialPanelStateFromObject(object, settings);
  const materialType =
    resolveMaterialType(settings?.materialType, settings?.shader) ||
    readMaterialTypeFromObject(object);

  const storedAlbedo = settings?.albedo;
  const albedo =
    live.albedo ||
    (!isPlaceholderAlbedo(storedAlbedo) ? storedAlbedo : '') ||
    '';

  return {
    ...(settings || {}),
    ...surface,
    materialType,
    castShadow:
      settings?.castShadow !== undefined
        ? settings.castShadow
        : shadow.castShadow,
    receiveShadow:
      settings?.receiveShadow !== undefined
        ? settings.receiveShadow
        : shadow.receiveShadow,
    side: settings?.side ?? surface.side,
    opacity: settings?.opacity ?? settings?.alpha ?? surface.opacity,
    alpha: settings?.alpha ?? settings?.opacity ?? surface.alpha,
    transparency:
      settings?.transparency !== undefined
        ? settings.transparency
        : surface.transparency,
    albedo,
    metallic: {
      metalness:
        live.metallic?.metalness ??
        settings?.metallic?.metalness ??
        0.5,
      roughness:
        live.metallic?.roughness ??
        settings?.metallic?.roughness ??
        0.5,
      map: settings?.metallic?.map ?? live.metallic?.map ?? '',
    },
    emission: live.emission || settings?.emission || '',
    emissionType:
      live.emissionType === true ||
      settings?.emissionType === true ||
      !!settings?.textureImage,
    metalicType: live.metalicType ?? settings?.metalicType ?? false,
    renderingMode:
      settings?.renderingMode ||
      inferRenderingModeFromObject(object) ||
      'Opaque',
    blending: settings?.blending ?? live.blending ?? 'Normal',
    ...(meshes ? { meshes } : {}),
    updatedonce: true,
    __selectionId: selectionId,
    __isUserEdit: false,
    __lastChangedKey: null,
  };
}

/** Keys stored on userData / API — excludes React-only panel state. */
const MATERIAL_PANEL_INTERNAL_KEYS = new Set([
  '__selectionId',
  '__isUserEdit',
  '__lastChangedKey',
  'meshes',
  'updatedonce',
]);

function normalizeMetallicForPayload(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return {
      metalness: typeof raw.metalness === 'number' ? raw.metalness : 0.5,
      roughness: typeof raw.roughness === 'number' ? raw.roughness : 0.5,
      map: raw.map ?? '',
    };
  }
  return { metalness: 0.5, roughness: 0.5, map: '' };
}

/** Deep-clone material_settings from live Three.js material + stored panel state. */
export function cloneMaterialSettingsForDuplicate(sourceObject, storedSettings) {
  if (!sourceObject) {
    const base =
      storedSettings && typeof storedSettings === 'object'
        ? JSON.parse(JSON.stringify(storedSettings))
        : {};
    return sanitizeMaterialPanelState(base);
  }

  const stored =
    storedSettings && typeof storedSettings === 'object'
      ? storedSettings
      : sourceObject.userData?.material_settings || {};

  const panelState = buildMaterialPanelStateForSelection(sourceObject, stored);
  const payload = buildMaterialSettingsPayload({
    ...stored,
    ...panelState,
    metallic: panelState.metallic,
  });

  const mapExtras = collectMapExportExtrasFromObject(sourceObject);
  if (Object.keys(mapExtras.mapVisibility || {}).length) {
    payload.mapVisibility = { ...mapExtras.mapVisibility };
  } else if (stored.mapVisibility) {
    payload.mapVisibility = { ...stored.mapVisibility };
  }

  if (Object.keys(mapExtras.mapPreviewUrls || {}).length) {
    payload.mapPreviewUrls = { ...mapExtras.mapPreviewUrls };
  } else if (stored.mapPreviewUrls) {
    payload.mapPreviewUrls = { ...stored.mapPreviewUrls };
  }

  const maps = { ...(stored.maps || {}) };
  collectMaterialMapList(sourceObject).forEach(({ key, previewUrl }) => {
    if (isPublishableTextureUrl(previewUrl)) maps[key] = previewUrl;
  });
  if (Object.keys(maps).length) payload.maps = maps;

  if (isPublishableTextureUrl(stored.textureImage)) {
    payload.textureImage = stored.textureImage;
  } else {
    const emissiveEntry = collectMaterialMapList(sourceObject).find(
      (entry) => entry.key === 'emissiveMap' && entry.previewUrl
    );
    if (isPublishableTextureUrl(emissiveEntry?.previewUrl)) {
      payload.textureImage = emissiveEntry.previewUrl;
    }
  }

  if (isPublishableTextureUrl(stored.NormalMap)) {
    payload.NormalMap = stored.NormalMap;
  } else {
    const normalEntry = collectMaterialMapList(sourceObject).find(
      (entry) => entry.key === 'normalMap' && entry.previewUrl
    );
    if (isPublishableTextureUrl(normalEntry?.previewUrl)) {
      payload.NormalMap = normalEntry.previewUrl;
    }
  }

  if (stored.normal_map && stored.normal_map !== 'Unselected') {
    payload.normal_map = stored.normal_map;
  } else if (payload.NormalMap || maps.normalMap) {
    payload.normal_map = 'Map';
  }

  if (isPublishableTextureUrl(maps.metalnessMap)) {
    payload.metallic = {
      ...normalizeMetallicForPayload(payload.metallic),
      map: maps.metalnessMap,
    };
  } else if (isPublishableTextureUrl(stored.metallic?.map)) {
    payload.metallic = {
      ...normalizeMetallicForPayload(payload.metallic),
      map: stored.metallic.map,
    };
  }

  const liveOverrides = collectChildMaterialOverrides(sourceObject);
  const childOverrides =
    (liveOverrides && Object.keys(liveOverrides).length
      ? liveOverrides
      : null) ||
    stored.childOverrides ||
    null;
  if (childOverrides && typeof childOverrides === 'object') {
    try {
      payload.childOverrides = JSON.parse(JSON.stringify(childOverrides));
    } catch {
      payload.childOverrides = childOverrides;
    }
  }

  return sanitizeMaterialPanelState(payload);
}

/** Merge Redux asset record with live scene object data for duplication. */
export function mergeDuplicateSourceItem(sceneObject, reduxItem = {}) {
  if (!sceneObject) return { ...(reduxItem || {}) };
  const ud = sceneObject.userData || {};
  return {
    ...(reduxItem || {}),
    _id: reduxItem?._id || sceneObject._id || sceneObject.name,
    id: reduxItem?.id || sceneObject._id || sceneObject.name,
    url: sceneObject.url || reduxItem?.url,
    temp_file_name: sceneObject.temp_file_name || reduxItem?.temp_file_name,
    name: sceneObject.file_name || sceneObject.name || reduxItem?.name,
    file_name: sceneObject.file_name || reduxItem?.file_name,
    asset_file_name:
      sceneObject.asset_file_name || reduxItem?.asset_file_name,
    asset_file_path:
      sceneObject.asset_file_path || reduxItem?.asset_file_path,
    mesh_code: sceneObject.mesh_code || ud.mesh_code || reduxItem?.mesh_code,
    glb_type: sceneObject.glb_type || ud.glb_type || reduxItem?.glb_type,
    localFile:
      sceneObject.localFile ||
      ud.duplicateSourceFile ||
      reduxItem?.localFile ||
      reduxItem?.userData?.duplicateSourceFile,
    originalFile:
      sceneObject.originalFile ||
      ud.duplicateSourceFile ||
      reduxItem?.originalFile ||
      reduxItem?.userData?.duplicateSourceFile,
    material_settings: cloneMaterialSettingsForDuplicate(
      sceneObject,
      ud.material_settings || reduxItem?.material_settings
    ),
  };
}

/** Apply synced material_settings from source onto a duplicated object. */
export function applyDuplicateMaterialToObject(target, source) {
  if (!target || !source) return null;

  copyMaterialMapsBetweenObjects(source, target);

  const settings = cloneMaterialSettingsForDuplicate(
    source,
    source.userData?.material_settings || target.userData?.material_settings
  );
  if (!settings || Object.keys(settings).length === 0) {
    syncMaterialMapBackupsFromSlots(target);
    return null;
  }

  // Always rebuild child overrides from live source materials so duplicates
  // match the viewport (stored JSON alone can lag behind shared-material edits).
  const liveChildOverrides = collectChildMaterialOverrides(source);
  if (liveChildOverrides && Object.keys(liveChildOverrides).length > 0) {
    settings.childOverrides = liveChildOverrides;
  }

  // Prefer live source appearance so stale shape defaults never overwrite clones.
  const live = readMaterialPanelStateFromObject(source, settings);
  if (live?.albedo) settings.albedo = live.albedo;
  if (live?.metallic) {
    settings.metallic = {
      ...normalizeMetallicForPayload(settings.metallic),
      ...live.metallic,
    };
  }
  if (live?.emission !== undefined) settings.emission = live.emission;

  target.userData = {
    ...(target.userData || {}),
    material_settings: settings,
    ...(source.userData?.threeMaterialEdited ? { threeMaterialEdited: true } : {}),
  };

  const hasEmbeddedMaps = objectHasEmbeddedGltfMaps(target);

  if (settings.mapVisibility && !hasEmbeddedMaps) {
    Object.entries(settings.mapVisibility).forEach(([mapKey, visible]) => {
      if (typeof visible === 'boolean') {
        setMaterialMapVisibility(target, mapKey, visible);
      }
    });
  }

  if (!hasEmbeddedMaps) {
    restoreMaterialMapsFromSettings(target, settings);
  } else {
    syncMaterialMapBackupsFromSlots(target);
  }

  // Materials were already cloned from the live source — do NOT UpdateMaterial.
  // That re-stamped stale albedo (default shape colors) and broke exact duplicates.
  if (settings.childOverrides && typeof settings.childOverrides === 'object') {
    applyChildMaterialOverrides(target, settings.childOverrides);
  }

  syncMaterialMapBackupsFromSlots(target);
  return settings;
}

/** Restore material settings after publish reload without stripping GLB textures. */
export async function applyMaterialSettingsAfterLoad(target, sourceObject, settings) {
  if (!target || !settings || typeof settings !== 'object') return null;

  const synced = cloneMaterialSettingsForDuplicate(
    sourceObject || target,
    settings
  );
  if (!synced || Object.keys(synced).length === 0) return null;

  target.userData = { ...(target.userData || {}), material_settings: synced };

  const mapSource = sourceObject || target;
  const hasEmbeddedMaps = objectHasEmbeddedGltfMaps(mapSource);

  if (synced.mapVisibility) {
    Object.entries(synced.mapVisibility).forEach(([mapKey, visible]) => {
      if (typeof visible === 'boolean') {
        setMaterialMapVisibility(mapSource, mapKey, visible);
      }
    });
  }

  if (!hasEmbeddedMaps) {
    await restoreMaterialMapsFromSettings(mapSource, synced);
  }

  UpdateMaterial({
    object: target,
    addData: {
      ...synced,
      __lastChangedKey: 'restore',
    },
  });

  // Object-level Render View / Side / Shadow (restore path used to skip these).
  applyObjectLevelMaterialSettingsOnLoad(target, synced);

  if (synced.childOverrides && typeof synced.childOverrides === 'object') {
    applyChildMaterialOverrides(target, synced.childOverrides);
  }

  return synced;
}

/**
 * Full material_settings payload for undo, userData, and publish API.
 * Pass the Material panel state (addData) or stored material_settings.
 */
/** Merge stored settings with live Three.js material surface values for publish/API. */
export function mergeMaterialSettingsForPublish(storedSettings, object) {
  const stored = storedSettings || {};
  const surface = readMaterialSurfaceFromObject(object);
  const shadow = readShadowFromObject(object);
  const mapExtras = object ? collectMapExportExtrasFromObject(object) : {};

  const maps = { ...(stored.maps || {}) };
  if (object) {
    collectMaterialMapList(object).forEach(({ key, previewUrl }) => {
      if (isPublishableTextureUrl(previewUrl)) maps[key] = previewUrl;
    });
  }

  const mergedPreviewUrls = {
    ...(stored.mapPreviewUrls || {}),
    ...(mapExtras.mapPreviewUrls || {}),
  };
  const publishablePreviewUrls = {};
  Object.entries(mergedPreviewUrls).forEach(([key, url]) => {
    if (isPublishableTextureUrl(url)) publishablePreviewUrls[key] = url;
  });

  const publishableMaps = {};
  Object.entries(maps).forEach(([key, url]) => {
    if (isPublishableTextureUrl(url)) publishableMaps[key] = url;
  });

  return sanitizeMaterialPanelState({
    ...stored,
    side: stored.side ?? surface.side,
    transparency:
      stored.transparency !== undefined
        ? stored.transparency
        : surface.transparency,
    opacity: stored.opacity ?? stored.alpha ?? surface.opacity,
    alpha: stored.alpha ?? stored.opacity ?? surface.alpha,
    castShadow:
      stored.castShadow !== undefined ? stored.castShadow : shadow.castShadow,
    receiveShadow:
      stored.receiveShadow !== undefined
        ? stored.receiveShadow
        : shadow.receiveShadow,
    mapVisibility: {
      ...(stored.mapVisibility || {}),
      ...(mapExtras.mapVisibility || {}),
    },
    ...(Object.keys(publishablePreviewUrls).length
      ? { mapPreviewUrls: publishablePreviewUrls }
      : {}),
    ...(Object.keys(publishableMaps).length ? { maps: publishableMaps } : {}),
  });
}

export function buildMaterialSettingsPayload(source, options = {}) {
  if (!source || typeof source !== 'object') return {};

  const { includeMeta = false, changedKey } = options;
  const opacity =
    source.opacity !== undefined
      ? source.opacity
      : source.alpha !== undefined
        ? source.alpha
        : 1;

  const materialType = resolveMaterialType(source.materialType, source.shader);

  const payload = {
    renderingMode: source.renderingMode ?? 'Opaque',
    materialType,
    type: materialType,
    shader: TYPE_TO_LEGACY_SHADER[materialType] ?? source.shader ?? 'Standard',
    albedo: source.albedo ?? '#ffffff',
    alpha: opacity,
    opacity,
    side: source.side ?? 'Front',
    blending: source.blending ?? 'Normal',
    emission: source.emission ?? '',
    emissionType: !!source.emissionType,
    metallic: normalizeMetallicForPayload(source.metallic),
    metalicType: !!source.metalicType,
    normal_map: source.normal_map ?? 'Unselected',
    transparency: !!source.transparency,
    textureImage: source.textureImage ?? '',
    aoMapIntensity:
      source.aoMapIntensity !== undefined ? source.aoMapIntensity : 1,
    mirror: !!source.mirror,
    castShadow: source.castShadow !== undefined ? !!source.castShadow : true,
    receiveShadow:
      source.receiveShadow !== undefined ? !!source.receiveShadow : true,
  };

  if (source.syncedFromGlb) payload.syncedFromGlb = true;
  if (source.added) payload.added = true;

  if (includeMeta && changedKey) {
    payload.__lastChangedKey = changedKey;
  }

  return payload;
}

/** Strip internal panel-only fields before persisting to userData / API. */
export function sanitizeMaterialPanelState(state) {
  if (!state || typeof state !== 'object') return {};
  const out = buildMaterialSettingsPayload(state);
  Object.keys(state).forEach((key) => {
    if (!MATERIAL_PANEL_INTERNAL_KEYS.has(key) && !(key in out)) {
      out[key] = state[key];
    }
  });
  return out;
}

function stripNonPublishableTextureFields(payload, source = {}) {
  if (!isPublishableTextureUrl(payload.textureImage)) {
    payload.textureImage = '';
  }
  if (!isPublishableTextureUrl(payload.NormalMap)) {
    payload.NormalMap = '';
    if (payload.normal_map === 'Map') {
      payload.normal_map = 'Unselected';
    }
  }
  if (payload.metallic && typeof payload.metallic === 'object') {
    if (!isPublishableTextureUrl(payload.metallic.map)) {
      payload.metallic = { ...payload.metallic, map: '' };
    }
  }

  if (source.syncedFromGlb) {
    const hasUserTexture =
      isPublishableTextureUrl(source.textureImage) ||
      isPublishableTextureUrl(source.NormalMap) ||
      isPublishableTextureUrl(source.metallic?.map) ||
      (source.maps &&
        typeof source.maps === 'object' &&
        Object.values(source.maps).some((url) => isPublishableTextureUrl(url)));

    if (!hasUserTexture) {
      payload.textureImage = '';
      payload.NormalMap = '';
      payload.normal_map = 'Unselected';
      if (payload.metallic) {
        payload.metallic = { ...payload.metallic, map: '' };
      }
    }
  }

  return payload;
}

/** Normalize merged material_settings for publish API (metallic object, no editor map state). */
export function sanitizeMaterialSettingsForApi(settings) {
  if (!settings || typeof settings !== 'object') return {};
  const out = buildMaterialSettingsPayload(settings);
  MATERIAL_PANEL_INTERNAL_KEYS.forEach((key) => delete out[key]);
  delete out.maps;
  delete out.mapVisibility;
  delete out.mapPreviewUrls;
  delete out.syncedFromGlb;
  delete out.added;
  // Do not send per-child override blobs on publish — materials are baked into the GLB.
  delete out.childOverrides;
  delete out.child_material_data;
  delete out.wireframe_mesh_indices;
  return stripNonPublishableTextureFields(out, settings);
}

/**
 * Object-level material panel fields for mesh publish.
 * Also keeps non-glTF maps (alphaMap) when a durable URL exists — GLTFExporter
 * cannot write Three.js alphaMap, so reload restores from these URLs.
 */
export function pickObjectLevelMaterialSettingsForApi(settings) {
  const src = settings && typeof settings === 'object' ? settings : {};
  const out = {
    renderingMode: src.renderingMode ?? 'Opaque',
    side: src.side ?? 'Front',
    castShadow: src.castShadow !== undefined ? !!src.castShadow : true,
    receiveShadow:
      src.receiveShadow !== undefined ? !!src.receiveShadow : true,
  };

  if (src.transparency !== undefined) {
    out.transparency = src.transparency;
  }

  const maps = {};
  const previews = {};
  const srcMaps = src.maps && typeof src.maps === 'object' ? src.maps : {};
  const srcPreviews =
    src.mapPreviewUrls && typeof src.mapPreviewUrls === 'object'
      ? src.mapPreviewUrls
      : {};
  // Persist every durable map URL. Native glTF maps also re-bake into the GLB;
  // non-native maps (alpha/specular/light/env/displacement) restore from these
  // URLs and/or GLB material extras (editorEmbeddedMaps).
  const ALL_MAP_KEYS = [
    'map',
    'alphaMap',
    'normalMap',
    'roughnessMap',
    'metalnessMap',
    'emissiveMap',
    'aoMap',
    'bumpMap',
    'displacementMap',
    'lightMap',
    'specularMap',
    'envMap',
  ];
  ALL_MAP_KEYS.forEach((key) => {
    const url = srcMaps[key] || srcPreviews[key];
    if (isPublishableTextureUrl(url)) {
      maps[key] = url;
      previews[key] = url;
    }
  });
  if (Object.keys(maps).length) {
    out.maps = maps;
    out.mapPreviewUrls = previews;
  }
  if (maps.alphaMap || src.transparency) {
    out.transparency =
      out.transparency !== undefined ? out.transparency : true;
  }

  return out;
}

/** Apply Side to all mesh materials on an object (does not touch maps/albedo). */
export function applySideSettingsToObject(object, sideLabel) {
  if (!object || sideLabel === undefined || sideLabel === null) return;
  const threeSide = labelToThreeSide(sideLabel);
  const applyMat = (material) => {
    if (!material) return;
    if (Array.isArray(material)) {
      material.forEach(applyMat);
      return;
    }
    material.side = threeSide;
    material.needsUpdate = true;
  };
  if (object.isMesh || object.isSkinnedMesh) applyMat(object.material);
  object.traverse?.((child) => {
    if (child.isMesh || child.isSkinnedMesh) applyMat(child.material);
  });
}

/**
 * Store + apply object-level settings from API/UI.
 * Render View changes the 3D grid only — material list previews stay authored.
 */
export function applyObjectLevelMaterialSettingsOnLoad(object, settings) {
  if (!object || !settings || typeof settings !== 'object') return;

  const level = pickObjectLevelMaterialSettingsForApi(settings);
  const prev = object.userData?.material_settings || {};

  // Persist only object-level (+ pivot flags). Drop albedo/maps so publish
  // does not treat them as material edits and re-bake the GLB.
  object.userData = {
    ...(object.userData || {}),
    material_settings: {
      ...level,
      ...(prev.pivot_mode ? { pivot_mode: prev.pivot_mode } : {}),
      ...(prev.native_pivot ? { native_pivot: prev.native_pivot } : {}),
      ...(prev.pivot_edited ? { pivot_edited: prev.pivot_edited } : {}),
    },
  };

  applyShadowSettingsToObject(object, {
    castShadow: level.castShadow,
    receiveShadow: level.receiveShadow,
  });
  applySideSettingsToObject(object, level.side);
  applyRenderingModeToObject(object, level.renderingMode);
}

/** Apply Render View to scene meshes (viewport). Does not affect list preview fingerprint. */
export function applyRenderingModeToObject(object, mode) {
  if (!object || !mode) return;
  const seen = new Set();
  const apply = (material) => {
    if (!material) return;
    if (Array.isArray(material)) {
      material.forEach(apply);
      return;
    }
    if (seen.has(material.uuid)) return;
    seen.add(material.uuid);
    try {
      // Replace userData if frozen (Immer) so Render View backups can be stored.
      const ud = material.userData || {};
      material.userData = { ...ud };
      applyRenderingMode(material, mode, object);
    } catch (e) {
      console.warn('applyRenderingModeToObject:', e);
    }
  };
  if (object.isMesh || object.isSkinnedMesh) apply(object.material);
  object.traverse?.((child) => {
    if (child.isMesh || child.isSkinnedMesh) apply(child.material);
  });
}

/**
 * Live UI update for object-level props. Never runs UpdateMaterial / never
 * rewrites GLB material slots via albedo/maps.
 */
export function applyObjectLevelPropToAsset(object, key, value, currentProps) {
  if (!object) return null;
  const next = {
    renderingMode: currentProps?.renderingMode ?? 'Opaque',
    side: currentProps?.side ?? 'Front',
    castShadow:
      currentProps?.castShadow !== undefined ? !!currentProps.castShadow : true,
    receiveShadow:
      currentProps?.receiveShadow !== undefined
        ? !!currentProps.receiveShadow
        : true,
    [key]: value,
  };

  const prev = object.userData?.material_settings || {};
  object.userData = {
    ...(object.userData || {}),
    material_settings: {
      renderingMode: next.renderingMode,
      side: next.side,
      castShadow: !!next.castShadow,
      receiveShadow: !!next.receiveShadow,
      ...(prev.pivot_mode ? { pivot_mode: prev.pivot_mode } : {}),
      ...(prev.native_pivot ? { native_pivot: prev.native_pivot } : {}),
      ...(prev.pivot_edited ? { pivot_edited: prev.pivot_edited } : {}),
    },
  };

  if (key === 'castShadow' || key === 'receiveShadow') {
    applyShadowSettingsToObject(object, {
      castShadow: next.castShadow,
      receiveShadow: next.receiveShadow,
    });
  }
  if (key === 'side') {
    applySideSettingsToObject(object, next.side);
  }
  if (key === 'renderingMode') {
    // Viewport only — list previews ignore Render View via originalMaterialProps.
    applyRenderingModeToObject(object, next.renderingMode);
  }

  return next;
}

export const UpdateMaterial = ({ object, addData }) => {
  if (!object || !addData) return;
  const isGLB = object?.type === 'Group' || object?.type === 'Scene';
  const objectType = determineObjectType(object);
  const changedKey = addData.__lastChangedKey;
  const isRestore = changedKey === 'restore';
  const resolvedMaterialType = resolveMaterialType(
    addData.materialType,
    addData.shader
  );
  const applyMaterialTypeForThisUpdate =
    !!resolvedMaterialType &&
    (!changedKey ||
      changedKey === 'materialType' ||
      changedKey === 'shader');
  const applyRenderingModeForThisUpdate = false; // Render View is metadata-only; never mutates materials.

  const updateMaterialSettings = (targetObject) => {
    if (!targetObject) return;

    const currentSettings = targetObject.userData?.material_settings || {};

    const merged = {
      ...currentSettings,
      ...addData,
      metallic: {
        ...normalizeMetallicForPayload(currentSettings.metallic),
        ...(addData.metallic && typeof addData.metallic === 'object'
          ? addData.metallic
          : {}),
      },
    };

    // Replace the whole userData object so Immer-frozen objects don't throw.
    // Direct property assignment (targetObject.userData.material_settings = X)
    // crashes when userData was frozen by Redux/Immer via addSceneMesh.
    targetObject.userData = {
      ...(targetObject.userData || {}),
      material_settings: sanitizeMaterialPanelState(merged),
    };
  };

  const applyMaterialProperties = (material, data = addData) => {
    if (!material) return;
    if (Array.isArray(material)) {
      material.forEach((m) => applyMaterialProperties(m, data));
      return;
    }

    try {
      if (changedKey === 'metallic') {
        if (data?.metallic?.roughness !== undefined && data.metallic.roughness >= 0) {
          material.roughness = Math.max(0.05, Math.min(1, data.metallic.roughness));
        }
        if (material.metalness !== undefined && data?.metallic?.metalness !== undefined) {
          material.metalness = Math.min(1.0, Math.max(0, data.metallic.metalness));
        }
        if (material.userData?.originalMaterialProps) {
          if (material.metalness !== undefined) {
            material.userData.originalMaterialProps.metalness = material.metalness;
          }
          if (material.roughness !== undefined) {
            material.userData.originalMaterialProps.roughness = material.roughness;
          }
        }
        if (data?.metallic?.map) {
          updateObjeUrl({
            object,
            addData: data,
            url: data.metallic.map,
            type: 'metallic',
          });
        }
        material.needsUpdate = true;
        return;
      }

      if (changedKey === 'side') {
        if (data.side !== undefined) {
          material.side = labelToThreeSide(data.side);
        }
        material.needsUpdate = true;
        return;
      }

      if (changedKey === 'transparency') {
        material.transparent = !!data.transparency;
        material.depthWrite = !material.transparent;
        material.needsUpdate = true;
        return;
      }

      if (changedKey === 'opacity' || changedKey === 'alpha') {
        const op = Math.max(
          0,
          Math.min(1, data.opacity ?? data.alpha ?? material.opacity ?? 1)
        );
        material.opacity = op;
        material.needsUpdate = true;
        return;
      }

      if (changedKey === 'blending') {
        if (data.blending !== undefined) {
          material.blending = labelToThreeBlending(data.blending);
        }
        material.needsUpdate = true;
        return;
      }

      if (changedKey === 'aoMapIntensity') {
        if (material.aoMapIntensity !== undefined && data.aoMapIntensity != null) {
          material.aoMapIntensity = data.aoMapIntensity;
        }
        material.needsUpdate = true;
        return;
      }

      if (changedKey === 'mapVisibility') {
        material.needsUpdate = true;
        return;
      }

      if (changedKey === 'duplicate' || changedKey === 'restore') {
        if (material.color && data && 'albedo' in data) {
          applyAlbedoColorToMaterial(material, data.albedo);
        }
        if (
          data?.metallic?.roughness !== undefined &&
          data.metallic.roughness >= 0
        ) {
          material.roughness = Math.max(
            0.05,
            Math.min(1, data.metallic.roughness)
          );
        }
        if (
          material.metalness !== undefined &&
          data?.metallic?.metalness !== undefined
        ) {
          material.metalness = Math.min(
            1.0,
            Math.max(0, data.metallic.metalness)
          );
        }
        if (material.opacity !== undefined) {
          const op =
            data.opacity !== undefined
              ? data.opacity
              : data.alpha !== undefined
                ? data.alpha
                : material.opacity;
          material.opacity = Math.max(0, Math.min(1, op ?? 1));
        }
        if (data.transparency !== undefined) {
          material.transparent = !!data.transparency;
          material.depthWrite = !material.transparent;
        }
        if (data.side !== undefined) {
          material.side = labelToThreeSide(data.side);
        }
        if (data.blending !== undefined) {
          material.blending = labelToThreeBlending(data.blending);
        }
        if (
          material.aoMapIntensity !== undefined &&
          data.aoMapIntensity != null
        ) {
          material.aoMapIntensity = data.aoMapIntensity;
        }
        if (data.emission && material.emissive) {
          material.emissive.set(data.emission);
          material.emissiveIntensity = 0.5;
        }
        material.needsUpdate = true;
        return;
      }

      if (changedKey === 'albedo') {
        applyAlbedoColorToMaterial(material, data?.albedo);
        return;
      }

      // Apply color whenever albedo is defined (use fallback for empty so object updates)
      if (material.color && data && 'albedo' in data) {
        applyAlbedoColorToMaterial(material, data.albedo);
      }

      // Only apply metalness and roughness directly
      if (data?.metallic?.roughness !== undefined && data.metallic.roughness >= 0) {
        material.roughness = Math.max(0.05, Math.min(1, data.metallic.roughness));
      }
      if (material.metalness !== undefined && data?.metallic?.metalness !== undefined) {
        material.metalness = Math.min(1.0, Math.max(0, data.metallic.metalness));
      }

      if (material.opacity !== undefined) {
        const op =
          data.opacity !== undefined
            ? data.opacity
            : data.alpha !== undefined
              ? data.alpha
              : material.opacity;
        material.opacity = Math.max(0, Math.min(1, op ?? 1));
      }

      if (data.transparency !== undefined) {
        material.transparent = !!data.transparency;
        material.depthWrite = !material.transparent;
      }

      if (data.side !== undefined) {
        material.side = labelToThreeSide(data.side);
      }

      if (data.blending !== undefined) {
        material.blending = labelToThreeBlending(data.blending);
      }

      if (
        material.aoMapIntensity !== undefined &&
        data.aoMapIntensity != null
      ) {
        material.aoMapIntensity = data.aoMapIntensity;
      }

      if (data.normal_map === 'Unselected') {
        material.normalMap = null;
        material.normalScale?.set(1, 1);
      }

      if (data.emission && material.emissive) {
        material.emissive.set(data.emission);
        material.emissiveIntensity = 0.5;
      } else if (!data.emission && material.emissive) {
        material.emissive.set('#000000');
        material.emissiveIntensity = 0;
        if (material.emissiveMap !== undefined && material.emissiveMap) {
          material.emissiveMap = null;
        }
      }

      // Handle texture updates (skip editor-only blob/data preview URLs)
      if (isPublishableTextureUrl(data?.NormalMap)) {
        updateObjeUrl({
          object,
          addData: data,
          url: data.NormalMap,
          type: 'NormalMap',
        });
      }

      if (isPublishableTextureUrl(data?.textureImage)) {
        updateObjeUrl({
          object,
          addData: data,
          url: data.textureImage,
          type: 'emission',
        });
      }

      if (isPublishableTextureUrl(data?.metallic?.map)) {
        updateObjeUrl({
          object,
          addData: data,
          url: data.metallic.map,
          type: 'metallic',
        });
      }

      // Rendering mode is applied once in a single pass at the end of UpdateMaterial (avoids duplicate traverse + allows chunked async)
      // if (data.renderingMode) applyRenderingMode(material, data.renderingMode);

      material.needsUpdate = true;
    } catch (error) {
      console.error('Error applying material properties:', error);
    }
  };

  const traverseAndUpdate = (object) => {
    if (object.isMesh && object.material) {
      updateMaterialSettings(object);
      applyMaterialProperties(object.material);
    }
    if (object.children?.length) {
      object.children.forEach((child) => traverseAndUpdate(child));
    }
  };

  try {
    if (objectType === 'Group' || objectType === 'Scene') {
      // Store material settings at GROUP level for UI to read
      updateMaterialSettings(object);

      // Apply material properties to all child meshes
      traverseAndUpdate(object);

      if (applyMaterialTypeForThisUpdate) {
        applyMaterialType(object, resolvedMaterialType);
      }
    } else if (objectType === 'Mesh') {
      updateMaterialSettings(object);
      applyMaterialProperties(object.material);

      if (applyMaterialTypeForThisUpdate) {
        applyMaterialType(object, resolvedMaterialType);
      }
    }

    // Mark edited so skinned GLB publish bakes / persists childOverrides.
    if (addData?.__isUserEdit || addData?.__lastChangedKey) {
      markMaterialEditedInHierarchy(object);
      let p = object.parent;
      while (p) {
        markMaterialEditedInHierarchy(p);
        p = p.parent;
      }
      // Shared-material siblings must also be flagged so publish restores all.
      markSharedMaterialSiblingMeshes(object);
      syncChildMaterialOverridesToAssetRoot(object);
    }
  } catch (error) {
    console.error('Error in UpdateMaterial:', error);
  }

  const shouldApplyShadow =
    !changedKey ||
    changedKey === 'castShadow' ||
    changedKey === 'receiveShadow' ||
    isRestore;
  if (shouldApplyShadow) {
    applyShadowSettingsToObject(object, {
      castShadow: addData.castShadow,
      receiveShadow: addData.receiveShadow,
    });
  }

  // Single pass: apply rendering mode (and re-apply after applyShader). Chunked to avoid blocking the UI.
  if (applyRenderingModeForThisUpdate) {
    const materials = [];
    if (isGLB) {
      object.traverse((child) => {
        if (child.isMesh && child.material) materials.push(child.material);
      });
    } else if (object.material) {
      materials.push(object.material);
    }
    const mode = addData.renderingMode;
    const chunkSize = 40;
    let index = 0;
    const runChunk = () => {
      const end = Math.min(index + chunkSize, materials.length);
      for (let i = index; i < end; i++) {
        try {
          applyRenderingMode(materials[i], mode, object);
        } catch (e) {
          console.warn('applyRenderingMode error', e);
        }
      }
      index = end;
      if (index < materials.length) {
        requestAnimationFrame(runChunk);
      } else {
        reapplyMaterialSurfaceFromSettings(materials, addData);
      }
    };
    if (materials.length > 0) {
      requestAnimationFrame(runChunk);
    } else if (
      addData.side !== undefined ||
      addData.transparency !== undefined ||
      addData.opacity !== undefined ||
      addData.alpha !== undefined
    ) {
      const materials = [];
      if (isGLB) {
        object.traverse((child) => {
          if (child.isMesh && child.material) materials.push(child.material);
        });
      } else if (object.material) {
        materials.push(object.material);
      }
      reapplyMaterialSurfaceFromSettings(materials, addData);
    }
  }

  const removeReflectorFrom = (target) => {
    const toRemove = [];
    target.traverse((child) => {
      if (child.userData?.isReflector) toRemove.push(child);
    });
    toRemove.forEach((r) => {
      try {
        if (r.parent) r.parent.remove(r);
        r.geometry?.dispose();
        r.material?.dispose();
      } catch (e) { }
    });
  };

  if (addData.mirror && typeof addReflectorTo === 'function') {
    addReflectorTo(object);
  } else if (!addData.mirror) {
    removeReflectorFrom(object);
  }

  // -----------------------------------------------------------------------
  // Sync material_settings back to Redux sceneMeshes for library/sketchfab
  // assets.  Without this, the Redux record stays empty after the user edits
  // colour/material, so the first publish sends empty material_settings to
  // the API and the changes vanish after publish.
  //
  // We resolve the "root pivot" of the edited object (walk up to the Group
  // that has source/libraryAssetId set) so that even edits made on a child
  // mesh propagate up to the correct Redux record.
  // -----------------------------------------------------------------------
  try {
    const isUserEdit = !!(addData?.__isUserEdit || addData?.__lastChangedKey);
    if (isUserEdit) {
      // Find the root Group pivot (the object registered in sceneMeshes)
      let pivot = object;
      while (pivot.parent && !pivot.parent.isScene) {
        pivot = pivot.parent;
      }

      const isLibraryOrSketchfab =
        pivot.source === 'library' ||
        pivot.source === 'sketchfab' ||
        pivot.userData?.source === 'library' ||
        pivot.userData?.source === 'sketchfab' ||
        pivot.userData?.from_library === true;

      if (isLibraryOrSketchfab) {
        const pivotId = pivot.name || pivot._id;
        const latestSettings = pivot.userData?.material_settings;
        if (pivotId && latestSettings) {
          store.dispatch(
            updateSceneMeshMaterialSettings({
              id: pivotId,
              material_settings: latestSettings,
            })
          );
        }
      }
    }
  } catch (_syncErr) {
    // Never let Redux sync crash the material update
  }
};

const UPLOAD_TYPE_TO_MAP_KEY = {
  NormalMap: 'normalMap',
  emission: 'emissiveMap',
  baseColor: 'map',
  albedo: 'map',
  metallic: 'metalnessMap',
  roughness: 'roughnessMap',
  alpha: 'alphaMap',
  ao: 'aoMap',
  light: 'lightMap',
  specular: 'specularMap',
  bump: 'bumpMap',
  displacement: 'displacementMap',
  env: 'envMap',
  envMap: 'envMap',
};

export const updateObjeUrl = ({ url, type, object, addData }) => {
  if (!url || !object) return Promise.resolve();

  const textureLoader = new TextureLoader();
  const mapKey = UPLOAD_TYPE_TO_MAP_KEY[type];

  return new Promise((resolve) => {
    textureLoader.load(
      url,
      (texture) => {
        if (!object) {
          resolve();
          return;
        }

        const isGLB = object?.type === 'Group' || object?.type === 'Scene';

        const applyTexture = (material) => {
          if (!material) return;

          try {
            const oldTexture = getOldTexture(material, type, mapKey);
            const oldIsTexture = !!(oldTexture && oldTexture.isTexture);
            configureMaterialMapTexture(
              texture,
              mapKey,
              oldIsTexture ? oldTexture : null
            );

            if (
              oldIsTexture &&
              oldTexture !== texture &&
              typeof oldTexture.dispose === 'function' &&
              !isTextureReferencedOnMaterial(material, oldTexture, mapKey)
            ) {
              try {
                oldTexture.dispose();
              } catch {
                /* ignore dispose of already-invalid textures */
              }
            }

            if (mapKey) {
              const ud = ensureMapUserData(material);
              ud[MAP_BACKUP_USERDATA_KEY][mapKey] = texture;
              ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = true;
              ud[MAP_PREVIEW_URLS_USERDATA_KEY][mapKey] = url;
            }

            if (addData?.normal_map === 'Map' && type === 'NormalMap') {
              material.normalMap = texture;
              material.normalScale?.set(1, 1);
            } else if (type === 'emission') {
              // Only set emissiveMap if the material supports it (not MeshBasicMaterial)
              if (material.emissiveMap !== undefined) {
                material.emissiveMap = texture;
                material.emissiveIntensity = 1;
                material.emissive?.set('#FFFFFF');
              }
            } else if (type === 'baseColor' || type === 'albedo') {
              material.map = texture;
              // GLTF often multiplies baseColorFactor with the map — keep white so colors match.
              if (material.color?.isColor && material.color.getHex() === 0x000000) {
                material.color.set(0xffffff);
              }
            } else if (type === 'metallic') {
              material.metalnessMap = texture;
            } else if (type === 'roughness') {
              material.roughnessMap = texture;
            } else if (type === 'alpha') {
              material.alphaMap = texture;
              material.transparent = true;
              if (material.depthWrite !== undefined) {
                material.depthWrite = false;
              }
            } else if (type === 'ao') {
              material.aoMap = texture;
            } else if (type === 'light') {
              material.lightMap = texture;
            } else if (type === 'specular') {
              material.specularMap = texture;
            } else if (type === 'bump') {
              material.bumpMap = texture;
            } else if (type === 'displacement') {
              material.displacementMap = texture;
            } else if (type === 'env' || type === 'envMap') {
              material.envMap = texture;
              if (material.envMapRotation == null) {
                material.envMapRotation = new THREE.Euler();
              }
            }

            material.needsUpdate = true;
          } catch (error) {
            console.error('Error applying texture:', error);
          }
        };

        if (isGLB) {
          const isUserTextureEdit =
            addData?.__isUserEdit === true ||
            addData?.__lastChangedKey === 'maps' ||
            addData?.__lastChangedKey === 'metallic' ||
            addData?.__lastChangedKey === 'NormalMap' ||
            addData?.__lastChangedKey === 'duplicate' ||
            addData?.__lastChangedKey === 'alpha' ||
            type === 'alpha';

          object.traverse((child) => {
            if (!child.isMesh && !child.isSkinnedMesh) return;
            const materials = Array.isArray(child.material)
              ? child.material
              : [child.material];
            materials.forEach((material) => {
              if (
                !isUserTextureEdit &&
                mapKey &&
                materialHasMapTexture(material, mapKey)
              ) {
                return;
              }
              applyTexture(material);
            });
          });
        } else {
          const materials = Array.isArray(object.material)
            ? object.material
            : [object.material];
          materials.forEach((material) => applyTexture(material));
        }

        // Force GLB re-bake on publish so alpha/maps reconstruct from the file.
        markMaterialEditedInHierarchy(object);
        let p = object.parent;
        while (p) {
          markMaterialEditedInHierarchy(p);
          p = p.parent;
        }
        markSharedMaterialSiblingMeshes(object);
        resolve();
      },
      undefined,
      (error) => {
        console.error('Error loading texture:', error);
        resolve();
      }
    );
  });
};

// Helper function to get old texture for disposal (slot or backup).
const getOldTexture = (material, type, mapKey) => {
  const backups = material.userData?.[MAP_BACKUP_USERDATA_KEY] || {};
  const fromSlot =
    mapKey && (material[mapKey] || backups[mapKey])
      ? material[mapKey] || backups[mapKey]
      : null;
  if (fromSlot?.isTexture) return fromSlot;

  let candidate = null;
  switch (type) {
    case 'NormalMap':
      candidate = material.normalMap;
      break;
    case 'emission':
      candidate = material.emissiveMap;
      break;
    case 'baseColor':
    case 'albedo':
      candidate = material.map;
      break;
    case 'metallic':
      candidate = material.metalnessMap;
      break;
    case 'roughness':
      candidate = material.roughnessMap;
      break;
    case 'alpha':
      candidate = material.alphaMap;
      break;
    case 'ao':
      candidate = material.aoMap;
      break;
    case 'light':
      candidate = material.lightMap;
      break;
    case 'specular':
      candidate = material.specularMap;
      break;
    case 'bump':
      candidate = material.bumpMap;
      break;
    case 'displacement':
      candidate = material.displacementMap;
      break;
    case 'env':
    case 'envMap':
      candidate = material.envMap;
      break;
    default:
      candidate = null;
  }
  return candidate?.isTexture ? candidate : null;
};

const WIREFRAME_TEMP_COLOR_KEY = '_wireframeColorBackup';
const WIREFRAME_TEMP_EMISSIVE_KEY = '_wireframeEmissiveBackup';
const WIREFRAME_TEMP_EMISSIVE_INTENSITY_KEY = '_wireframeEmissiveIntensityBackup';
const WIREFRAME_TEMP_TRANSMISSION_KEY = '_wireframeTransmissionBackup';
/** Bright line color so dark/textured GLB base colors don't vanish as wires. */
const WIREFRAME_VISIBLE_HEX = 0xffffff;

function restoreWireframeTemporaryOverrides(material) {
  if (!material?.userData) return;
  const ud = material.userData;
  if (ud[WIREFRAME_TEMP_COLOR_KEY] !== undefined && material.color) {
    try {
      material.color.setHex(ud[WIREFRAME_TEMP_COLOR_KEY]);
    } catch {
      /* ignore */
    }
    delete ud[WIREFRAME_TEMP_COLOR_KEY];
  }
  if (ud[WIREFRAME_TEMP_EMISSIVE_KEY] !== undefined && material.emissive) {
    try {
      material.emissive.setHex(ud[WIREFRAME_TEMP_EMISSIVE_KEY]);
    } catch {
      /* ignore */
    }
    delete ud[WIREFRAME_TEMP_EMISSIVE_KEY];
  }
  if (
    ud[WIREFRAME_TEMP_EMISSIVE_INTENSITY_KEY] !== undefined &&
    material.emissiveIntensity !== undefined
  ) {
    material.emissiveIntensity = ud[WIREFRAME_TEMP_EMISSIVE_INTENSITY_KEY];
    delete ud[WIREFRAME_TEMP_EMISSIVE_INTENSITY_KEY];
  }
  if (
    ud[WIREFRAME_TEMP_TRANSMISSION_KEY] !== undefined &&
    material.transmission !== undefined
  ) {
    material.transmission = ud[WIREFRAME_TEMP_TRANSMISSION_KEY];
    delete ud[WIREFRAME_TEMP_TRANSMISSION_KEY];
  }
}

/**
 * Wireframe Render View: keep the object's texture/color on the wires.
 * - With a map: color → white so albedo paints the lines (don't wash with emissive).
 * - Without a map: brighten very dark albedo so thin wires stay visible.
 */
function ensureWireframeVisibleAppearance(material) {
  if (!material) return;
  material.userData = material.userData || {};
  const ud = material.userData;
  const hasAlbedoMap = !!getValidTexture(material.map);

  if (material.color && ud[WIREFRAME_TEMP_COLOR_KEY] === undefined) {
    ud[WIREFRAME_TEMP_COLOR_KEY] = material.color.getHex();
    if (hasAlbedoMap) {
      // Multiply map at full strength so wires show the texture.
      material.color.setHex(0xffffff);
    } else {
      const lum =
        material.color.r * 0.2126 +
        material.color.g * 0.7152 +
        material.color.b * 0.0722;
      if (lum < 0.2) {
        material.color.setHex(WIREFRAME_VISIBLE_HEX);
      }
    }
  }

  // Textured wireframe: leave emissive alone (white emissive washes the map).
  // Untextured dark meshes: slight emissive so lines read in a dark scene.
  if (
    !hasAlbedoMap &&
    material.emissive &&
    ud[WIREFRAME_TEMP_EMISSIVE_KEY] === undefined
  ) {
    const lum =
      (material.color?.r ?? 1) * 0.2126 +
      (material.color?.g ?? 1) * 0.7152 +
      (material.color?.b ?? 1) * 0.0722;
    if (lum < 0.35) {
      ud[WIREFRAME_TEMP_EMISSIVE_KEY] = material.emissive.getHex();
      ud[WIREFRAME_TEMP_EMISSIVE_INTENSITY_KEY] =
        typeof material.emissiveIntensity === 'number'
          ? material.emissiveIntensity
          : 1;
      material.emissive.setHex(WIREFRAME_VISIBLE_HEX);
      material.emissiveIntensity = Math.max(
        material.emissiveIntensity || 0,
        0.25
      );
    }
  }

  if (
    material.transmission !== undefined &&
    material.transmission > 0 &&
    ud[WIREFRAME_TEMP_TRANSMISSION_KEY] === undefined
  ) {
    ud[WIREFRAME_TEMP_TRANSMISSION_KEY] = material.transmission;
    material.transmission = 0;
  }
}

/**
 * Restore surface props saved before a Render View change.
 * Render View is display-only — these backups exist so Opaque/Original/export
 * can undo Cutout/Fade/Wireframe without baking them into the GLB.
 */
export function restoreFromOriginalMaterialProps(material) {
  if (!material) return false;
  const orig = material.userData?.originalMaterialProps;
  if (!orig) return false;
  try {
    if (orig.color !== undefined && material.color) {
      material.color.setHex(orig.color);
    }
    if (material.metalness !== undefined && orig.metalness !== undefined) {
      material.metalness = orig.metalness;
    }
    if (material.roughness !== undefined && orig.roughness !== undefined) {
      material.roughness = orig.roughness;
    }
    if (orig.emissive !== undefined && material.emissive) {
      material.emissive.setHex(orig.emissive);
    }
    if (
      material.emissiveIntensity !== undefined &&
      orig.emissiveIntensity !== undefined
    ) {
      material.emissiveIntensity = orig.emissiveIntensity;
    }
    if (orig.opacity !== undefined) material.opacity = orig.opacity;
    if (orig.transparent !== undefined) material.transparent = orig.transparent;
    if (orig.alphaTest !== undefined) material.alphaTest = orig.alphaTest;
    if (orig.depthWrite !== undefined) material.depthWrite = orig.depthWrite;
    if (orig.blending !== undefined && orig.blending !== null) {
      material.blending = orig.blending;
    }
    if (orig.side !== undefined && orig.side !== null) {
      material.side = orig.side;
    }
    if (orig.wireframe !== undefined) material.wireframe = orig.wireframe;
    material.needsUpdate = true;
    return true;
  } catch (err) {
    console.warn('restoreFromOriginalMaterialProps failed:', err);
    return false;
  }
}

/**
 * Strip temporary Render View mutations before GLTF export / publish.
 * Keeps authored materials intact; renderingMode stays in material_settings JSON only.
 */
export function stripRenderingViewOverridesForExport(material) {
  if (!material) return;
  if (Array.isArray(material)) {
    material.forEach(stripRenderingViewOverridesForExport);
    return;
  }
  restoreWireframeTemporaryOverrides(material);
  if (material.userData?.originalMaterialProps) {
    restoreFromOriginalMaterialProps(material);
  }
  // Heal false Cutout (alphaTest with no alpha source) even when no backup exists.
  if (
    typeof material.alphaTest === 'number' &&
    material.alphaTest > 1e-4 &&
    !material.alphaMap
  ) {
    material.alphaTest = 0;
    const opacity =
      typeof material.opacity === 'number' ? material.opacity : 1;
    if (opacity >= 1 - 1e-6) {
      material.transparent = false;
      if (material.depthWrite !== undefined) material.depthWrite = true;
    }
  }
  material.needsUpdate = true;
}

/** True when Cutout can be previewed without discarding the whole mesh. */
function materialHasCutoutAlphaSource(material) {
  if (!material) return false;
  if (material.alphaMap) return true;
  const alphaTest =
    typeof material.alphaTest === 'number' ? material.alphaTest : 0;
  const origAlpha =
    typeof material.userData?.originalMaterialProps?.alphaTest === 'number'
      ? material.userData.originalMaterialProps.alphaTest
      : 0;
  // Already a MASK material in the GLB — safe to preview Cutout.
  if (alphaTest > 1e-4 || origAlpha > 1e-4) return true;
  return false;
}

export const applyRenderingMode = (material, mode, object = null) => {
  if (!material) return;
  if (Array.isArray(material)) {
    material.forEach((m) => applyRenderingMode(m, mode, object));
    return;
  }
  // Save original material properties before first renderingMode change.
  // Only save scalar/color values — never texture references (map, normalMap, etc.),
  // as those can be disposed when materials are swapped and cause crashes when restored.
  if (
    mode !== 'Original' &&
    mode !== 'Skeleton' &&
    mode !== 'Opaque' &&
    (!material.userData || !material.userData.originalMaterialProps)
  ) {
    material.userData = material.userData || {};
    material.userData.originalMaterialProps = {
      color: material.color ? material.color.getHex() : undefined,
      metalness: material.metalness,
      roughness: material.roughness,
      emissive: material.emissive ? material.emissive.getHex() : undefined,
      emissiveIntensity: material.emissiveIntensity,
      opacity: material.opacity,
      transparent: material.transparent,
      alphaTest: material.alphaTest,
      depthWrite: material.depthWrite,
      blending: material.blending,
      side: material.side,
      wireframe: material.wireframe,
    };
  }
  try {
    // Leaving Wireframe: restore temporary brightening before other mode props.
    if (mode !== 'Wireframe') {
      restoreWireframeTemporaryOverrides(material);
    }

    switch (mode) {
      case 'Opaque':
        // View reset: restore authored surface when we have a Render View backup.
        // Fresh loads have no backup — leave GLB materials alone (do not force opaque).
        if (material.userData?.originalMaterialProps) {
          restoreFromOriginalMaterialProps(material);
        } else {
          material.wireframe = false;
        }
        if (object?.userData?.skeletonHelper) {
          object.userData.skeletonHelper.visible = false;
        }
        break;

      case 'Cutout':
        // Preview only when an alpha source exists. Applying alphaTest=0.5 on
        // opaque albedo maps discards most fragments → mesh/materials "disappear".
        if (materialHasCutoutAlphaSource(material)) {
          material.transparent = true;
          material.alphaTest =
            typeof material.alphaTest === 'number' && material.alphaTest > 1e-4
              ? material.alphaTest
              : 0.5;
          material.opacity = 1;
          material.depthWrite = false;
          material.blending = Three.NormalBlending;
        }
        material.wireframe = false;
        if (object?.userData?.skeletonHelper) {
          object.userData.skeletonHelper.visible = false;
        }
        break;

      case 'Fade':
        material.transparent = true;
        material.alphaTest = 0;
        material.opacity = 0.5;
        material.wireframe = false;
        material.depthWrite = true;
        material.blending = Three.NormalBlending;
        if (object?.userData?.skeletonHelper) {
          object.userData.skeletonHelper.visible = false;
        }
        break;

      case 'Skeleton': {
        if (object) {
          material.transparent = true;
          material.opacity = 0.6;
          material.alphaTest = 0;
          material.wireframe = false;
          material.depthWrite = true;
          material.blending = Three.NormalBlending;

          if (!object.userData) object.userData = {};
          // Only attach a helper when the hierarchy actually has bones —
          // SkeletonHelper on a plain mesh Group can crash the render loop.
          let hasBones = false;
          object.traverse?.((child) => {
            if (child.isBone || child.isSkinnedMesh) hasBones = true;
          });
          if (hasBones && !object.userData.skeletonHelper) {
            try {
              const helper = new SkeletonHelper(object);
              object.userData.skeletonHelper = helper;
              if (object.parent) {
                object.parent.add(helper);
              } else {
                object.add(helper);
              }
            } catch (e) {
              console.warn('SkeletonHelper create failed:', e);
            }
          }
          if (object.userData.skeletonHelper) {
            object.userData.skeletonHelper.visible = true;
          }
        }
        break;
      }

      case 'Original': {
        if (!restoreFromOriginalMaterialProps(material)) {
          material.transparent = false;
          material.alphaTest = 0;
          material.depthWrite = true;
          material.blending = Three.NormalBlending;
          material.opacity = 1;
          material.wireframe = false;
        }
        if (object?.userData?.skeletonHelper) {
          object.userData.skeletonHelper.visible = false;
        }
        break;
      }

      case 'Wireframe':
        material.transparent = false;
        material.alphaTest = 0;
        material.depthWrite = true;
        material.blending = Three.NormalBlending;
        material.opacity = 1;
        material.wireframe = true;
        ensureWireframeVisibleAppearance(material);
        if (object?.userData?.skeletonHelper) {
          object.userData.skeletonHelper.visible = false;
        }
        break;

      default:
        console.warn('Unknown rendering mode:', mode);
    }

    material.needsUpdate = true;
  } catch (error) {
    console.error('Error applying rendering mode:', error);
  }
};

function getValidTexture(texture) {
  if (!texture) return null;
  try {
    if (texture.image || texture.source) return texture;
  } catch {
    return null;
  }
  return null;
}

function copyCommonSurfaceProps(oldMat, newMat) {
  newMat.transparent = oldMat.transparent;
  newMat.opacity = oldMat.opacity;
  newMat.side = oldMat.side;
  if (oldMat.alphaTest !== undefined) newMat.alphaTest = oldMat.alphaTest;
  if (oldMat.depthWrite !== undefined) newMat.depthWrite = oldMat.depthWrite;
  if (oldMat.blending !== undefined) newMat.blending = oldMat.blending;
  if (oldMat.premultipliedAlpha !== undefined) {
    newMat.premultipliedAlpha = oldMat.premultipliedAlpha;
  }
  if (oldMat.wireframe !== undefined) newMat.wireframe = oldMat.wireframe;
}

function collectTextures(oldMat) {
  return {
    map: getValidTexture(oldMat.map),
    alphaMap: getValidTexture(oldMat.alphaMap),
    envMap: getValidTexture(oldMat.envMap),
    lightMap: getValidTexture(oldMat.lightMap),
    aoMap: getValidTexture(oldMat.aoMap),
    bumpMap: getValidTexture(oldMat.bumpMap),
    normalMap: getValidTexture(oldMat.normalMap),
    displacementMap: getValidTexture(oldMat.displacementMap),
    emissiveMap: getValidTexture(oldMat.emissiveMap),
    specularMap: getValidTexture(oldMat.specularMap),
    roughnessMap: getValidTexture(oldMat.roughnessMap),
    metalnessMap: getValidTexture(oldMat.metalnessMap),
    matcap: getValidTexture(oldMat.matcap),
    gradientMap: getValidTexture(oldMat.gradientMap),
  };
}

function createMaterialOfType(materialType, oldMat) {
  const color = oldMat.color?.clone?.() || new Three.Color(0xffffff);
  const emissive =
    oldMat.emissive?.clone?.() || new Three.Color(0x000000);
  const tex = collectTextures(oldMat);
  const type = resolveMaterialType(materialType, null);

  const base = { color, transparent: oldMat.transparent, opacity: oldMat.opacity };

  switch (type) {
    case 'MeshBasicMaterial':
      return new Three.MeshBasicMaterial({
        ...base,
        map: tex.emissiveMap || tex.map,
        alphaMap: tex.alphaMap,
        aoMap: tex.aoMap,
        envMap: tex.envMap,
        lightMap: tex.lightMap,
      });
    case 'MeshDepthMaterial':
      return new Three.MeshDepthMaterial({
        depthPacking: oldMat.depthPacking ?? Three.RGBADepthPacking,
      });
    case 'MeshNormalMaterial':
      return new Three.MeshNormalMaterial({
        normalMap: tex.normalMap,
        bumpMap: tex.bumpMap,
      });
    case 'MeshLambertMaterial':
      return new Three.MeshLambertMaterial({
        ...base,
        map: tex.map,
        emissive,
        emissiveMap: tex.emissiveMap,
        emissiveIntensity: oldMat.emissiveIntensity ?? 1,
        normalMap: tex.normalMap,
        bumpMap: tex.bumpMap,
        aoMap: tex.aoMap,
        envMap: tex.envMap,
        lightMap: tex.lightMap,
      });
    case 'MeshMatcapMaterial':
      return new Three.MeshMatcapMaterial({
        color,
        matcap: tex.matcap || tex.map,
        normalMap: tex.normalMap,
        bumpMap: tex.bumpMap,
      });
    case 'MeshPhongMaterial':
      return new Three.MeshPhongMaterial({
        ...base,
        map: tex.map,
        emissive,
        emissiveMap: tex.emissiveMap,
        emissiveIntensity: oldMat.emissiveIntensity ?? 1,
        specularMap: tex.specularMap,
        normalMap: tex.normalMap,
        bumpMap: tex.bumpMap,
        aoMap: tex.aoMap,
        envMap: tex.envMap,
        lightMap: tex.lightMap,
        shininess: oldMat.shininess ?? 30,
      });
    case 'MeshToonMaterial':
      return new Three.MeshToonMaterial({
        ...base,
        map: tex.map,
        normalMap: tex.normalMap,
        bumpMap: tex.bumpMap,
        gradientMap: tex.gradientMap,
      });
    case 'MeshPhysicalMaterial':
      return new Three.MeshPhysicalMaterial({
        ...base,
        map: tex.map,
        metalness: oldMat.metalness ?? 0.5,
        roughness: oldMat.roughness ?? 0.5,
        clearcoat: oldMat.clearcoat ?? 0,
        emissive,
        emissiveIntensity: oldMat.emissiveIntensity ?? 0,
        emissiveMap: tex.emissiveMap,
        normalMap: tex.normalMap,
        normalScale: oldMat.normalScale,
        metalnessMap: tex.metalnessMap,
        roughnessMap: tex.roughnessMap,
        aoMap: tex.aoMap,
        alphaMap: tex.alphaMap,
        envMap: tex.envMap,
        lightMap: tex.lightMap,
      });
    case 'MeshStandardMaterial':
      return new Three.MeshStandardMaterial({
        ...base,
        map: tex.map,
        metalness: oldMat.metalness ?? 0.5,
        roughness: oldMat.roughness ?? 0.5,
        emissive,
        emissiveIntensity: oldMat.emissiveIntensity ?? 0,
        emissiveMap: tex.emissiveMap,
        normalMap: tex.normalMap,
        normalScale: oldMat.normalScale,
        metalnessMap: tex.metalnessMap,
        roughnessMap: tex.roughnessMap,
        aoMap: tex.aoMap,
        alphaMap: tex.alphaMap,
        envMap: tex.envMap,
        lightMap: tex.lightMap,
      });
    case 'ShadowMaterial':
      return new Three.ShadowMaterial({
        opacity: oldMat.opacity ?? 1,
        color: color,
      });
    case 'ShaderMaterial':
    case 'RawShaderMaterial':
      console.warn(
        `${type} requires custom GLSL; using MeshBasicMaterial with current maps.`
      );
      return new Three.MeshBasicMaterial({
        ...base,
        map: tex.map || tex.emissiveMap,
      });
    default:
      console.warn('Unknown material type:', type);
      return null;
  }
}

function markMaterialEditedInHierarchy(object) {
  if (!object) return;
  object.userData = { ...(object.userData || {}), threeMaterialEdited: true };
}

/** Flag every mesh that uses the same material instance(s) as `object`. */
function markSharedMaterialSiblingMeshes(object) {
  if (!object) return;

  const materials = new Set();
  const collect = (obj) => {
    if (!obj) return;
    if ((obj.isMesh || obj.isSkinnedMesh) && obj.material) {
      const list = Array.isArray(obj.material) ? obj.material : [obj.material];
      list.forEach((m) => m && materials.add(m));
    }
  };
  collect(object);
  object.traverse?.(collect);
  if (!materials.size) return;

  let root = object;
  while (root.parent) {
    if (
      root.deleteType === 'mesh' ||
      root.objType === '3d' ||
      root.glb_type === 'shapes' ||
      root.userData?.isShapes
    ) {
      break;
    }
    root = root.parent;
  }

  root.traverse?.((obj) => {
    if (!obj.isMesh && !obj.isSkinnedMesh) return;
    const list = Array.isArray(obj.material) ? obj.material : [obj.material];
    if (list.some((m) => materials.has(m))) {
      markMaterialEditedInHierarchy(obj);
    }
  });
}

export const applyMaterialType = (object, materialType) => {
  if (!object || !materialType) return;
  const resolved = resolveMaterialType(materialType, materialType);

  const applyToMaterial = (mesh) => {
    if (!mesh.material) return;

    const replaceOne = (oldMat) => {
      if (!oldMat || oldMat.type === resolved) return oldMat;

      const newMaterial = createMaterialOfType(resolved, oldMat);
      if (!newMaterial) return oldMat;

      copyCommonSurfaceProps(oldMat, newMaterial);
      if (
        newMaterial.aoMapIntensity !== undefined &&
        oldMat.aoMapIntensity != null
      ) {
        newMaterial.aoMapIntensity = oldMat.aoMapIntensity;
      }

      oldMat.dispose();
      newMaterial.needsUpdate = true;
      return newMaterial;
    };

    try {
      if (Array.isArray(mesh.material)) {
        mesh.material = mesh.material.map(replaceOne);
      } else {
        mesh.material = replaceOne(mesh.material);
      }
    } catch (error) {
      console.error('Error applying material type:', error);
    }
  };

  if (object.type === 'Group' || object.type === 'Scene') {
    markMaterialEditedInHierarchy(object);
    object.traverse((child) => {
      if (child.isMesh && child.material) {
        markMaterialEditedInHierarchy(child);
        applyToMaterial(child);
      }
    });
  } else if (object.material) {
    markMaterialEditedInHierarchy(object);
    applyToMaterial(object);
  }
};

/** @deprecated Use applyMaterialType */
export const applyShader = (object, shaderType) => {
  applyMaterialType(object, resolveMaterialType(null, shaderType));
};










// import { TextureLoader } from 'three';
// import * as Three from 'three';
// import { Reflector } from 'three/examples/jsm/objects/Reflector.js';

// export const determineObjectType = (object) => {
//   if (object?.isMesh) {
//     return 'Mesh';
//   } else if (object?.type === 'Group' || object?.type === 'Scene') {
//     return 'Group';
//   }
//   return 'Unknown';
// };

// export const UpdateMaterial = ({ object, addData }) => {
//   if (!object || !addData) return;
//   const isGLB = object?.type === 'Group' || object?.type === 'Scene';
//   const objectType = determineObjectType(object);

//   const updateMaterialSettings = (targetObject) => {
//     if (!targetObject) return;

//     targetObject.userData = targetObject.userData || {};
//     const currentSettings = targetObject.userData.material_settings || {};

//     // Only keep metalness and roughness, remove metallic toggle
//     const metallicMetalness =
//       addData.metallic?.metalness !== undefined
//         ? addData.metallic.metalness
//         : currentSettings.metallic?.metalness;

//     const metallicRoughness =
//       addData.metallic?.roughness !== undefined
//         ? addData.metallic.roughness
//         : currentSettings.metallic?.roughness;

//     targetObject.userData.material_settings = {
//       ...currentSettings,
//       renderingMode: addData.renderingMode,
//       shader: addData.shader,
//       albedo: addData.albedo,
//       alpha: addData.alpha,
//       emission: addData.emission,
//       metallic: {
//         ...(currentSettings.metallic || {}),
//         map: addData.metallic?.map || currentSettings.metallic?.map || null,
//         metalness: metallicMetalness,
//         roughness: metallicRoughness,
//       },
//       normal_map: addData.normal_map,
//       transparency: addData.transparency,
//       textureImage: addData.textureImage || '',
//       mirror: addData.mirror,
//     };
//   };

//   const applyMaterialProperties = (material, data = addData) => {
//     if (!material) return;

//     try {
//       if (material.color && data.albedo) {
//         material.color.set(data.albedo);
//       }

//       // Only apply metalness and roughness directly
//       if (data?.metallic?.roughness !== undefined && data.metallic.roughness >= 0) {
//         material.roughness = Math.max(0.05, Math.min(1, data.metallic.roughness));
//       }
//       if (material.metalness !== undefined && data?.metallic?.metalness !== undefined) {
//         material.metalness = Math.min(1.0, Math.max(0, data.metallic.metalness));
//       }

//       if (material.opacity !== undefined) {
//         material.opacity = data.alpha ?? 1;
//         material.transparent = (data.alpha ?? 1) < 1;
//       }

//       if (data.normal_map === 'Unselected') {
//         material.normalMap = null;
//         material.normalScale?.set(1, 1);
//       }

//       if (data.emission && material.emissive) {
//         material.emissive.set(data.emission);
//         material.emissiveIntensity = 0.5;
//       } else if (!data.emission && material.emissive) {
//         material.emissive.set('#000000');
//         material.emissiveIntensity = 0;
//         if (material.emissiveMap !== undefined && material.emissiveMap) {
//           material.emissiveMap = null;
//         }
//       }

//       // Handle texture updates
//       if (data?.NormalMap) {
//         updateObjeUrl({
//           object,
//           addData: data,
//           url: data.NormalMap,
//           type: 'NormalMap',
//         });
//       }

//       if (data?.textureImage) {
//         updateObjeUrl({
//           object,
//           addData: data,
//           url: data.textureImage,
//           type: 'emission', // Fixed: should be emission, not NormalMap
//         });
//       }

//       if (data?.metallic?.map) {
//         updateObjeUrl({
//           object,
//           addData: data,
//           url: data.metallic.map,
//           type: 'metallic',
//         });
//       }

//       if (data.renderingMode) {
//         applyRenderingMode(material, data.renderingMode);
//       }

//       material.needsUpdate = true;
//     } catch (error) {
//       console.error('Error applying material properties:', error);
//     }
//   };

//   const traverseAndUpdate = (object) => {
//     if (object.isMesh && object.material) {
//       updateMaterialSettings(object);
//       applyMaterialProperties(object.material);
//     }
//     if (object.children?.length) {
//       object.children.forEach((child) => traverseAndUpdate(child));
//     }
//   };

//   try {
//     if (objectType === 'Group' || objectType === 'Scene') {
//       // Store material settings at GROUP level for UI to read
//       updateMaterialSettings(object);

//       // Apply material properties to all child meshes
//       traverseAndUpdate(object);

//       // Handle shader changes for the entire group
//       if (addData.shader) {
//         applyShader(object, addData.shader);
//       }
//     } else if (objectType === 'Mesh') {
//       updateMaterialSettings(object);
//       applyMaterialProperties(object.material);

//       if (addData.shader) {
//         applyShader(object, addData.shader);
//       }
//     }
//   } catch (error) {
//     console.error('Error in UpdateMaterial:', error);
//   }

//   // Apply shader first, then reapply rendering mode so the shader swap doesn't discard rendering flags
//   if (addData.shader) {
//     applyShader(object, addData.shader);
//   }

//   if (addData.renderingMode) {
//     if (isGLB) {
//       object.traverse((child) => {
//         if (child.isMesh && child.material) {
//           applyRenderingMode(child.material, addData.renderingMode);
//         }
//       });
//     } else if (object.material) {
//       applyRenderingMode(object.material, addData.renderingMode);
//     }
//   }

//   const removeReflectorFrom = (target) => {
//     const toRemove = [];
//     target.traverse((child) => {
//       if (child.userData?.isReflector) toRemove.push(child);
//     });
//     toRemove.forEach((r) => {
//       try {
//         if (r.parent) r.parent.remove(r);
//         r.geometry?.dispose();
//         r.material?.dispose();
//       } catch (e) { }
//     });
//   };

//   if (addData.mirror) {
//     if (objectType === 'Group' || objectType === 'Scene') {
//       addReflectorTo(object);
//     } else {
//       addReflectorTo(object);
//     }
//   } else {
//     if (objectType === 'Group' || objectType === 'Scene') {
//       removeReflectorFrom(object);
//     } else {
//       removeReflectorFrom(object);
//     }
//   }
// };

// const updateObjeUrl = ({ url, type, object, addData }) => {
//   if (!url || !object) return;

//   const textureLoader = new TextureLoader();

//   textureLoader.load(
//     url,
//     (texture) => {
//       if (!object) return;

//       const isGLB = object?.type === 'Group' || object?.type === 'Scene';

//       const applyTexture = (material) => {
//         if (!material) return;

//         try {
//           // Dispose old texture to prevent memory leaks
//           const oldTexture = getOldTexture(material, type);
//           if (oldTexture) {
//             oldTexture.dispose();
//           }

//           if (addData?.normal_map === 'Map' && type === 'NormalMap') {
//             material.normalMap = texture;
//             material.normalScale?.set(1, 1);
//           } else if (type === 'emission') {
//             // Only set emissiveMap if the material supports it (not MeshBasicMaterial)
//             if (material.emissiveMap !== undefined) {
//               material.emissiveMap = texture;
//               material.emissiveIntensity = 1;
//               material.emissive?.set('#FFFFFF');
//             }
//           } else if (type === 'baseColor' || type === 'albedo') {
//             material.map = texture;
//           } else if (type === 'metallic') {
//             material.metalnessMap = texture;
//           } else if (type === 'roughness') {
//             material.roughnessMap = texture;
//           }

//           material.needsUpdate = true;
//         } catch (error) {
//           console.error('Error applying texture:', error);
//         }
//       };

//       if (isGLB) {
//         object.traverse((child) => {
//           if (child.isMesh) applyTexture(child.material);
//         });
//       } else {
//         applyTexture(object.material);
//       }
//     },
//     undefined,
//     (error) => {
//       console.error('Error loading texture:', error);
//     }
//   );
// };

// // Helper function to get old texture for disposal
// const getOldTexture = (material, type) => {
//   switch (type) {
//     case 'NormalMap':
//       return material.normalMap;
//     case 'emission':
//       return material.emissiveMap;
//     case 'baseColor':
//     case 'albedo':
//       return material.map;
//     case 'metallic':
//       return material.metalnessMap;
//     case 'roughness':
//       return material.roughnessMap;
//     default:
//       return null;
//   }
// };

// export const applyRenderingMode = (material, mode) => {
//   if (!material) return;
//   console.log(material, 'before applying mode', mode);
//   try {
//     switch (mode) {
//       case 'Opaque':
//         material.transparent = false;
//         material.alphaTest = 0;
//         material.depthWrite = true;
//         material.blending = Three.NormalBlending;
//         material.wireframe = false;
//         break;

//       case 'Cutout':
//         material.transparent = true;
//         material.alphaTest = 0.5;
//         material.opacity = 1;
//         material.depthWrite = false;
//         material.blending = Three.NormalBlending;
//         material.wireframe = false;
//         break;

//       case 'Fade':
//         material.transparent = true;
//         material.opacity = 0.5;
//         // material.alphaTest = 0;
//         // material.depthWrite = false;
//         // material.blending = Three.NormalBlending;
//         // material.wireframe = false;
//         break;

//       case 'Original':
//         // Restore all original material properties if available
//         console.log('Restoring original material properties:', material);
//         if (material.userData && material.userData.originalMaterialProps) {
//           const orig = material.userData.originalMaterialProps;
//           try {
//             if (orig.color !== undefined && material.color) material.color.setHex(orig.color);
//             if (orig.metalness !== undefined) material.metalness = orig.metalness;
//             if (orig.roughness !== undefined) material.roughness = orig.roughness;
//             if (orig.map !== undefined) material.map = orig.map;
//             if (orig.normalMap !== undefined) material.normalMap = orig.normalMap;
//             if (orig.roughnessMap !== undefined) material.roughnessMap = orig.roughnessMap;
//             if (orig.metalnessMap !== undefined) material.metalnessMap = orig.metalnessMap;
//             if (orig.aoMap !== undefined) material.aoMap = orig.aoMap;
//             if (orig.emissive !== undefined && material.emissive) material.emissive.setHex(orig.emissive);
//             if (orig.emissiveIntensity !== undefined) material.emissiveIntensity = orig.emissiveIntensity;
//             if (orig.emissiveMap !== undefined) material.emissiveMap = orig.emissiveMap;
//             if (orig.opacity !== undefined) material.opacity = orig.opacity;
//             if (orig.transparent !== undefined) material.transparent = orig.transparent;
//             if (orig.alphaTest !== undefined) material.alphaTest = orig.alphaTest;
//             if (orig.depthWrite !== undefined) material.depthWrite = orig.depthWrite;
//             if (orig.blending !== undefined) material.blending = orig.blending;
//             if (orig.side !== undefined) material.side = orig.side;
//             if (orig.wireframe !== undefined) material.wireframe = orig.wireframe;
//           } catch (err) {
//             console.warn('Failed to restore original material props in applyRenderingMode:', err);
//           }
//         }
//         else if (material) {
//           const orig = material;

//           try {
//             // --- COLOR ---
//             if (orig.color && material.color) {
//               material.color.copy(orig.color);
//             } else if (material.color) {
//               material.color.set(0xffffff); // default white
//             }

//             // --- METAL / ROUGH ---
//             material.metalness =
//               orig.metalness !== undefined ? orig.metalness : 0.0;

//             material.roughness =
//               orig.roughness !== undefined ? orig.roughness : 1.0;

//             // --- MAPS ---
//             material.map = orig.map || null;
//             material.normalMap = orig.normalMap || null;
//             material.roughnessMap = orig.roughnessMap || null;
//             material.metalnessMap = orig.metalnessMap || null;
//             material.aoMap = orig.aoMap || null;

//             // --- EMISSIVE ---
//             if (orig.emissive && material.emissive) {
//               material.emissive.copy(orig.emissive);
//             } else if (material.emissive) {
//               material.emissive.set(0x000000);
//             }

//             material.emissiveIntensity =
//               orig.emissiveIntensity !== undefined
//                 ? orig.emissiveIntensity
//                 : 1.0;

//             material.emissiveMap = orig.emissiveMap || null;

//             // --- TRANSPARENCY ---
//             material.opacity =
//               orig.opacity !== undefined ? orig.opacity : 1.0;

//             material.transparent =
//               orig.transparent !== undefined ? orig.transparent : false;

//             material.alphaTest =
//               orig.alphaTest !== undefined ? orig.alphaTest : 0.0;

//             // --- DEPTH / BLENDING ---
//             material.depthWrite =
//               orig.depthWrite !== undefined ? orig.depthWrite : true;

//             material.blending =
//               orig.blending !== undefined ? orig.blending : THREE.NormalBlending;

//             material.side =
//               orig.side !== undefined ? orig.side : THREE.FrontSide;

//             material.wireframe =
//               orig.wireframe !== undefined ? orig.wireframe : false;

//             // VERY IMPORTANT after changes
//             material.needsUpdate = true;

//           } catch (err) {
//             console.warn(
//               'Failed to restore original material props in applyRenderingMode:',
//               err
//             );
//           }
//         }

//         else {
//           // Fallback: set some defaults
//           material.transparent = true;
//           material.alphaTest = 0.5;
//           material.depthWrite = true;
//           material.opacity = 1;
//           material.wireframe = false;
//         }
//         break;

//       case 'Wireframe':
//         // Wireframe rendering: enable wireframe and use normal blending/depth settings
//         material.transparent = false;
//         material.alphaTest = 0;
//         material.depthWrite = true;
//         material.blending = Three.NormalBlending;
//         material.opacity = 1;
//         material.wireframe = true;
//         break;

//       default:
//         console.warn('Unknown rendering mode:', mode);
//     }

//     material.needsUpdate = true;
//   } catch (error) {
//     console.error('Error applying rendering mode:', error);
//   }
// };

// const applyShader = (object, shaderType) => {
//   if (!object) return;

//   const applyToMaterial = (mesh) => {
//     if (!mesh.material) return;

//     try {
//       const oldMat = mesh.material;
//       let newMaterial;

//       // Helper to check if texture is valid
//       const getValidTexture = (texture) => {
//         if (!texture) return null;
//         try {
//           if (texture.image || texture.source) {
//             return texture;
//           }
//         } catch (e) {
//           return null;
//         }
//         return null;
//       };

//       const validMap = getValidTexture(oldMat.map);
//       const validEmissiveMap = getValidTexture(oldMat.emissiveMap);
//       const validNormalMap = getValidTexture(oldMat.normalMap);
//       const validMetalnessMap = getValidTexture(oldMat.metalnessMap);

//       switch (shaderType) {
//         case 'Standard':
//           newMaterial = new Three.MeshStandardMaterial({
//             color: oldMat.color || new Three.Color(0xffffff),
//             map: validMap,
//             metalness: oldMat.metalness ?? 0.3,
//             roughness: oldMat.roughness ?? 0.6,
//             emissive: oldMat.emissive || new Three.Color(0x000000),
//             emissiveIntensity: oldMat.emissiveIntensity ?? 0,
//             emissiveMap: validEmissiveMap,
//             normalMap: validNormalMap,
//             normalScale: oldMat.normalScale,
//             metalnessMap: validMetalnessMap,
//           });
//           break;

//         case 'PBR':
//           newMaterial = new Three.MeshPhysicalMaterial({
//             color: oldMat.color || new Three.Color(0xffffff),
//             map: validMap,
//             metalness: oldMat.metalness ?? 0.5,
//             roughness: oldMat.roughness ?? 0.5,
//             clearcoat: oldMat.clearcoat ?? 0,
//             emissive: oldMat.emissive || new Three.Color(0x000000),
//             emissiveIntensity: oldMat.emissiveIntensity ?? 0,
//             emissiveMap: validEmissiveMap,
//             normalMap: validNormalMap,
//             normalScale: oldMat.normalScale,
//             metalnessMap: validMetalnessMap,
//           });
//           break;

//         case 'Unlit':
//           const finalMap = validEmissiveMap || validMap;
//           newMaterial = new Three.MeshBasicMaterial({
//             color: oldMat.color || new Three.Color(0xffffff),
//             map: finalMap,
//           });
//           break;

//         default:
//           console.warn('Unknown shader type:', shaderType);
//           return;
//       }

//       // Apply common properties
//       newMaterial.transparent = oldMat.transparent;
//       newMaterial.opacity = oldMat.opacity;
//       newMaterial.side = oldMat.side;

//       // Preserve rendering-related flags so renderingMode survives material swap (alphaTest, depthWrite, blending, etc.)
//       if (oldMat.alphaTest !== undefined) newMaterial.alphaTest = oldMat.alphaTest;
//       if (oldMat.depthWrite !== undefined) newMaterial.depthWrite = oldMat.depthWrite;
//       if (oldMat.blending !== undefined) newMaterial.blending = oldMat.blending;
//       if (oldMat.premultipliedAlpha !== undefined) newMaterial.premultipliedAlpha = oldMat.premultipliedAlpha;

//       oldMat.dispose();
//       mesh.material = newMaterial;
//       mesh.material.needsUpdate = true;
//     } catch (error) {
//       console.error('Error applying shader:', error);
//     }
//   };

//   if (object.type === 'Group' || object.type === 'Scene') {
//     object.traverse((child) => {
//       if (child.isMesh && child.material) {
//         applyToMaterial(child);
//       }
//     });
//   } else if (object.material) {
//     applyToMaterial(object);
//   }
// };